import * as React from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { cn } from "@/v2/lib/utils";
import { etMinutesOfDay } from "@/v2/lib/timezone";
import { useMediaQuery } from "@/v2/hooks/useMediaQuery";
import {
  absSpan,
  clampToGap,
  DAY_MINUTES,
  dayDiff,
  DEFAULT_ZOOM_INDEX,
  formatHHmm,
  snapMinutes,
  tickLabel,
  tickMinutesFor,
  toHHmm,
  toMinutes,
  validatePlacement,
  VISIBLE_START_HOUR,
  ZOOM_LEVELS,
} from "../schedulerUtils";
import type { Channel, DragPayload, Program } from "../types";
import type { TouchDragHandle } from "../useTouchDragBridge";
import { ChannelLabel } from "./ChannelLabel";
import { ProgramBlock } from "./ProgramBlock";

export const LABEL_WIDTH = 152;
/** Below sm, the channel label collapses to icon-only (see ChannelLabel):
 * the full name + "24/7 Channel" text left only an hour or two of the
 * timeline visible on a phone. */
const LABEL_WIDTH_COMPACT = 64;
const HEADER_HEIGHT = 64;
const ROW_HEIGHT = 96;

interface Props {
  channels: Channel[];
  programs: Program[];
  /** The strip's days, in order (one past day, then the selected date
   * onward). The grid renders them as one continuous timeline, so a block
   * may run across a midnight boundary and a drop can land on any day in
   * the strip, including yesterday's overnight hours. */
  dates: string[];
  /** The selected date: where the strip scrolls to on open (the past day
   * before it is reached by scrolling left). */
  openDate: string;
  /** Today's ET date key: places the current-time line inside the strip. */
  todayKey: string;
  now: Date;
  selectedId: string | null;
  dragRef: React.MutableRefObject<DragPayload | null>;
  /** Bridges touch input onto this grid's native-drag onDragOver/onDrop props
   *  (see useTouchDragBridge); omitted only in tests that don't exercise
   *  touch. */
  touchDrag?: TouchDragHandle;
  onSelect: (id: string) => void;
  onDropAt: (target: { channelId: string; date: string; startMin: number }) => void;
  onResizeEdge: (id: string, edge: "start" | "end", deltaMin: number) => void;
}

interface Preview {
  channelId: string;
  /** Absolute minutes on the strip axis, for positioning. */
  startAbs: number;
  endAbs: number;
  valid: boolean;
}

/** The grid is the fixed Eastern broadcast day, so the marker reads ET,
 * not the admin's own wall clock (an IST admin at 5:12 PM local is at
 * 7:42 AM on this grid). */
function nowLabel(now: Date) {
  return `${formatHHmm(toHHmm(etMinutesOfDay(now)))} ET`;
}

/** One of the timeline's zoom controls (minus, plus, reset). */
function ZoomButton({
  label,
  compact,
  disabled,
  onClick,
  children,
}: {
  label: string;
  compact: boolean;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const size = compact ? 18 : 28;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex shrink-0 items-center justify-center rounded-md text-muted-foreground ring-1 ring-border hover:bg-accent hover:text-foreground disabled:opacity-40"
      style={{ width: size, height: size }}
    >
      {children}
    </button>
  );
}

/** "Wed, Sep 24" for the day headings along the strip. */
function dayLabel(key: string) {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function TimelineGrid({
  channels,
  programs,
  dates,
  openDate,
  todayKey,
  now,
  selectedId,
  dragRef,
  touchDrag,
  onSelect,
  onDropAt,
  onResizeEdge,
}: Props) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [preview, setPreview] = React.useState<Preview | null>(null);
  const stripStart = dates[0];
  const stripMinutes = dates.length * DAY_MINUTES;
  const [zoomIndex, setZoomIndex] = React.useState(DEFAULT_ZOOM_INDEX);
  const pph = ZOOM_LEVELS[zoomIndex];
  const ppm = pph / 60;
  const trackWidth = dates.length * 24 * pph;
  const compact = useMediaQuery("(max-width: 639px)");
  const labelWidth = compact ? LABEL_WIDTH_COMPACT : LABEL_WIDTH;
  const nowAbs = dayDiff(todayKey, stripStart) * DAY_MINUTES + etMinutesOfDay(now);

  // Zoom is buttons only (minus, plus, reset), one step per click: a
  // trackpad pinch fired a burst of wheel events and jumped several levels
  // at once. The strip minute at the viewport centre is kept in place
  // across a zoom change, applied right after the re-render so the view
  // stays put instead of jumping.
  const zoomAnchor = React.useRef<{ minute: number; x: number } | null>(null);
  const zoomTo = React.useCallback(
    (next: number) => {
      const el = scrollRef.current;
      const clamped = Math.min(Math.max(next, 0), ZOOM_LEVELS.length - 1);
      if (clamped === zoomIndex) return;
      if (el) {
        const x = labelWidth + (el.clientWidth - labelWidth) / 2;
        zoomAnchor.current = { minute: (el.scrollLeft + x - labelWidth) / ppm, x };
      }
      setZoomIndex(clamped);
    },
    [zoomIndex, labelWidth, ppm],
  );
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    const anchor = zoomAnchor.current;
    if (!el || !anchor) return;
    el.scrollLeft = Math.max(0, anchor.minute * ppm - (anchor.x - labelWidth));
    zoomAnchor.current = null;
  }, [ppm, labelWidth]);

  // Tick labels are rendered for the visible window only: at the finest
  // zoom the strip holds thousands of minute ticks.
  const [viewport, setViewport] = React.useState({ left: 0, width: 1600 });
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let frame = 0;
    const read = () => {
      frame = 0;
      setViewport({ left: el.scrollLeft, width: el.clientWidth });
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Open at the selected day's midnight; the previous day is a scroll to
  // the left, the rest of the strip a scroll to the right. Only when the
  // day changes: a zoom keeps its own anchor instead.
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft =
        (dayDiff(openDate, stripStart) * 24 + VISIBLE_START_HOUR) * pph;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDate]);

  // Bring a newly placed or selected program into view if it is off screen.
  const selectedProgram = programs.find((p) => p.id === selectedId);
  const selectedStart = selectedProgram ? absSpan(selectedProgram, stripStart).start : null;
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || selectedStart === null) return;
    const left = selectedStart * ppm;
    const visibleWidth = el.clientWidth - labelWidth;
    if (left < el.scrollLeft || left > el.scrollLeft + visibleWidth - 60) {
      el.scrollLeft = Math.max(0, left - 40);
    }
    // Only when the selection changes, not while dragging or on a clock tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  /** Pointer x -> the block's start in absolute strip minutes, stopped
   * flush against the hovered channel's neighbouring blocks so dragging
   * into a clip pins the block at its boundary (a continuous stream)
   * instead of overlapping. New content, and an existing block dragged
   * leftwards, additionally snap left onto the end of the previous
   * content so the stream stays gapless; the snap is bounded to the
   * cursor's own day, so a drop on an empty day lands at its midnight
   * rather than flying back to the previous day's last block. Falls back
   * to the raw position, which the validity check then marks invalid,
   * when the gap cannot fit. */
  const computeStart = (
    e: React.DragEvent<HTMLDivElement>,
    drag: DragPayload,
    channelId: string,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const raw = Math.min(
      Math.max(snapMinutes((e.clientX - rect.left) / ppm - drag.grabOffsetMin), 0),
      stripMinutes - drag.durationMin,
    );
    const dragged =
      drag.kind === "program" ? programs.find((p) => p.id === drag.id) : undefined;
    const snapLeft =
      drag.kind === "content" || (dragged ? raw < absSpan(dragged, stripStart).start : false);
    const cursorDayStart = Math.floor(raw / DAY_MINUTES) * DAY_MINUTES;
    const clamped = clampToGap(
      programs,
      channelId,
      stripStart,
      raw,
      drag.durationMin,
      stripMinutes,
      drag.kind === "program" ? drag.id : undefined,
      snapLeft ? cursorDayStart : undefined,
    );
    return clamped ?? raw;
  };

  /** Absolute strip minutes -> the day it lands on and minutes within it. */
  const placeOf = (startAbs: number) => {
    const dayIndex = Math.min(Math.floor(startAbs / DAY_MINUTES), dates.length - 1);
    return { date: dates[dayIndex], startMin: startAbs - dayIndex * DAY_MINUTES };
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, channelId: string) => {
    const drag = dragRef.current;
    if (!drag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = drag.kind === "program" ? "move" : "copy";
    const startAbs = computeStart(e, drag, channelId);
    const { date, startMin } = placeOf(startAbs);
    // A content drag carries the game's own channel; hovering any other
    // channel's row previews as invalid before the drop is even attempted.
    const wrongChannel = drag.kind === "content" && !!drag.channelId && drag.channelId !== channelId;
    const valid =
      !wrongChannel &&
      validatePlacement(programs, {
        channelId,
        date,
        startMin,
        endMin: startMin + drag.durationMin,
        ignoreId: drag.kind === "program" ? drag.id : undefined,
      }) === null;
    setPreview((prev) =>
      prev && prev.channelId === channelId && prev.startAbs === startAbs && prev.valid === valid
        ? prev
        : { channelId, startAbs, endAbs: startAbs + drag.durationMin, valid },
    );
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, channelId: string) => {
    const drag = dragRef.current;
    setPreview(null);
    if (!drag) return;
    e.preventDefault();
    const { date, startMin } = placeOf(computeStart(e, drag, channelId));
    onDropAt({ channelId, date, startMin });
  };

  // Labelled ticks inside the visible window plus a screen of slack either
  // side, so a fast scroll never shows an empty scale.
  const tickMinutes = tickMinutesFor(pph);
  const visibleTicks = React.useMemo(() => {
    const slack = viewport.width;
    const from = Math.max(0, Math.floor((viewport.left - slack) / ppm / tickMinutes) * tickMinutes);
    const to = Math.min(stripMinutes, (viewport.left + viewport.width + slack) / ppm);
    const out: number[] = [];
    for (let m = from; m < to; m += tickMinutes) out.push(m);
    return out;
  }, [viewport, ppm, tickMinutes, stripMinutes]);

  return (
    <div ref={scrollRef} className="sched-scroll overflow-x-auto" data-testid="timeline-scroll">
      <div className="relative" style={{ width: labelWidth + trackWidth }}>
        {/* Time scale: a date heading at each day boundary, hour marks below. */}
        <div className="flex border-b border-border" style={{ height: HEADER_HEIGHT }}>
          <div
            className="sticky left-0 z-20 flex shrink-0 items-center justify-center bg-card"
            style={{ width: labelWidth, gap: compact ? 2 : 4 }}
          >
            <ZoomButton
              label="Zoom out"
              compact={compact}
              disabled={zoomIndex === 0}
              onClick={() => zoomTo(zoomIndex - 1)}
            >
              <Minus size={14} />
            </ZoomButton>
            {compact ? null : (
              <span className="text-xs text-muted-foreground tabular-nums" style={{ minWidth: 30, textAlign: "center" }}>
                {tickMinutes >= 60 ? `${tickMinutes / 60}h` : `${tickMinutes}m`}
              </span>
            )}
            <ZoomButton
              label="Zoom in"
              compact={compact}
              disabled={zoomIndex === ZOOM_LEVELS.length - 1}
              onClick={() => zoomTo(zoomIndex + 1)}
            >
              <Plus size={14} />
            </ZoomButton>
            <ZoomButton
              label="Reset zoom"
              compact={compact}
              disabled={zoomIndex === DEFAULT_ZOOM_INDEX}
              onClick={() => zoomTo(DEFAULT_ZOOM_INDEX)}
            >
              <RotateCcw size={13} />
            </ZoomButton>
          </div>
          <div className="relative" style={{ width: trackWidth }}>
            {dates.map((day, i) => (
              <span
                key={day}
                className={cn(
                  "absolute text-xs font-semibold whitespace-nowrap",
                  day === todayKey ? "text-action-hover" : "text-foreground",
                )}
                // A day heading scrolled past its start stays pinned to the
                // left edge while any of its day is on screen.
                style={{
                  left: Math.min(
                    Math.max(i * DAY_MINUTES * ppm, viewport.left) + 6,
                    (i + 1) * DAY_MINUTES * ppm - 120,
                  ),
                  top: 8,
                }}
                data-testid={`day-heading-${day}`}
              >
                {dayLabel(day)}
                {day === todayKey ? " · Today" : ""}
              </span>
            ))}
            {visibleTicks.map((abs) => (
              <span
                key={abs}
                className="absolute text-xs whitespace-nowrap text-muted-foreground"
                style={{ left: abs * ppm + 6, bottom: 10 }}
              >
                {tickLabel(abs % DAY_MINUTES)}
              </span>
            ))}
          </div>
        </div>

        {/* Channel rows */}
        {channels.map((channel) => {
          const rowPrograms = programs
            .filter((p) => {
              if (p.channelId !== channel.id) return false;
              const d = dayDiff(p.date, stripStart);
              // Day -1 is fetched for its possible spill-over: render it so
              // a block airing across the strip's first midnight is visible.
              return d >= -1 && d < dates.length;
            })
            .sort((a, b) => absSpan(a, stripStart).start - absSpan(b, stripStart).start);
          return (
            <div
              key={channel.id}
              className="flex border-b border-border last:border-b-0"
              style={{ height: ROW_HEIGHT }}
              data-testid={`row-${channel.id}`}
            >
              <div
                className="sticky left-0 z-20 flex shrink-0 items-center bg-card"
                style={{ width: labelWidth, padding: compact ? "0 10px" : "0 16px" }}
              >
                <ChannelLabel channel={channel} compact={compact} />
              </div>
              <div
                className="relative"
                style={{
                  width: trackWidth,
                  backgroundImage:
                    "linear-gradient(to right, var(--v2-border) 1px, transparent 1px)",
                  // One grid line per labelled tick, so the lines match the scale.
                  backgroundSize: `${tickMinutes * ppm}px 100%`,
                }}
                data-testid={`track-${channel.id}`}
                data-drop-target
                onDragOver={(e) => handleDragOver(e, channel.id)}
                onDragLeave={() => setPreview(null)}
                onDrop={(e) => handleDrop(e, channel.id)}
              >
                {rowPrograms.map((program) => {
                  // Where this block's edges may stretch to (strip-axis
                  // minutes): flush against the neighbouring blocks, or the
                  // strip walls. The block combines these with its own
                  // content limits (trim/runtime) while previewing.
                  const span = absSpan(program, stripStart);
                  const otherSpans = rowPrograms
                    .filter((p) => p.id !== program.id)
                    .map((p) => absSpan(p, stripStart));
                  const prevEnds = otherSpans.map((s) => s.end).filter((e) => e <= span.start);
                  const nextStarts = otherSpans.map((s) => s.start).filter((s) => s >= span.end);
                  return (
                    <ProgramBlock
                      key={program.id}
                      program={program}
                      offsetMin={dayDiff(program.date, stripStart) * DAY_MINUTES}
                      resizeBounds={{
                        minStart: prevEnds.length ? Math.max(...prevEnds) : 0,
                        maxEnd: nextStarts.length ? Math.min(...nextStarts) : stripMinutes,
                      }}
                      selected={program.id === selectedId}
                      height={ROW_HEIGHT}
                      pixelsPerHour={pph}
                      dragRef={dragRef}
                      touchDrag={touchDrag}
                      onSelect={onSelect}
                      onResizeEdge={onResizeEdge}
                    />
                  );
                })}
                {preview && preview.channelId === channel.id ? (
                  <div
                    aria-hidden
                    className={cn(
                      "pointer-events-none absolute rounded-md border-2 border-dashed",
                      preview.valid
                        ? "border-action bg-action-light"
                        : "border-destructive bg-destructive/10",
                    )}
                    style={{
                      left: preview.startAbs * ppm + 2,
                      width: (preview.endAbs - preview.startAbs) * ppm - 4,
                      top: 8,
                      height: ROW_HEIGHT - 16,
                    }}
                  />
                ) : null}
              </div>
            </div>
          );
        })}

        {/* Day boundaries: a heavier line where one day hands off to the next. */}
        {dates.slice(1).map((day, i) => (
          <div
            key={day}
            aria-hidden
            className="pointer-events-none absolute z-[5]"
            style={{
              left: labelWidth + (i + 1) * 24 * pph,
              top: 0,
              bottom: 0,
              width: 2,
              background: "var(--v2-border)",
            }}
          />
        ))}

        {/* Current time */}
        {nowAbs >= 0 && nowAbs <= stripMinutes ? (
          <div
            data-testid="current-time-indicator"
            className="pointer-events-none absolute z-10"
            style={{
              left: labelWidth + nowAbs * ppm,
              top: 0,
              bottom: 0,
              width: 2,
              background: "var(--v2-action-hover)",
            }}
          >
            <span
              className="absolute rounded-md bg-action-hover text-xs font-semibold text-white"
              style={{
                top: 4,
                left: 1,
                transform: "translateX(-50%)",
                padding: "4px 8px",
                whiteSpace: "nowrap",
              }}
            >
              {nowLabel(now)}
            </span>
            <span
              className="absolute rounded-full bg-action-hover"
              style={{ top: 34, left: 1, width: 8, height: 8, transform: "translateX(-50%)" }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
